/**
 * Recently viewed products — the two halves of one small feature.
 *
 *   RECORD  a product page carries `[data-airflux-recent]` (rendered by
 *           `snippets/airflux-recent.liquid`); this pushes it to the front of
 *           `airflux:recent` in localStorage.
 *   PAINT   `<airflux-recent>` in the header's search panel draws the list back
 *           as chips, on every page.
 *
 * Both halves live here because the file is loaded in exactly one place — the
 * search panel, which is in the header on every page — so recording needs no
 * second script tag on the product template and cannot fall out of step with
 * the thing that reads it.
 *
 * REAL PRODUCT PAGES ONLY. A resolved landing page shows a product without
 * being one, and counting those would fill this list with the same two rows
 * every visitor sees — the car band above the header is already the memory of
 * where somebody has been on those pages.
 *
 * With JavaScript off nothing here happens and the panel is what it always was:
 * the field, and the quick links under it.
 */
(() => {
  'use strict';

  const KEY = 'airflux:recent';

  /** How many are kept. One more than is ever shown, so that opening the panel
   *  ON a product page still has four to draw after that page is filtered out. */
  const CAP = 6;

  /** How many are drawn. Four is two rows of chips on a 390px phone; a fifth
   *  starts a third row and the panel's own quick links go below the fold. */
  const SHOWN = 4;

  /* Through `window.AirfluxStorage`, never `localStorage` directly.

     🔴 This list is the one piece of the theme's memory that is BROWSING
     HISTORY rather than something the visitor told us, and the ICO's guidance
     puts content chosen from "previous browsing history" outside the appearance
     exception. Within a visit it is the shopper's own trail through the shop
     and rides the same exemption as the car; across visits it is consent, same
     as everything else the module owns. The register in
     `snippets/airflux-storage.liquid` is what applies the difference. */
  const read = () => {
    const list = window.AirfluxStorage?.readJSON(KEY);
    return Array.isArray(list) ? list : [];
  };

  const write = (list) => window.AirfluxStorage?.writeJSON(KEY, list);

  /** The payload this page carries, if it is a product page at all. */
  function payload() {
    const el = document.querySelector('[data-airflux-recent]');
    if (!el) return null;
    try {
      const item = JSON.parse(el.textContent);
      return item?.h && item?.u ? item : null;
    } catch {
      return null;
    }
  }

  const here = payload();

  // Most recent first, no duplicates. Re-viewing a page moves it to the front
  // rather than adding a second copy of it.
  if (here) {
    const list = read().filter((p) => p?.h && p.h !== here.h);
    list.unshift(here);
    write(list.slice(0, CAP));
  }

  /**
   * 🔴 Everything below builds NODES and sets `textContent`.
   *
   * These values were in localStorage, which means the page that wrote them and
   * the page reading them are only related by a key name. Handing any of it to
   * an HTML parser would make a stored string executable on every page of the
   * store — the same rule `airflux-pdp.js` follows for the car label, for the
   * same reason.
   *
   * Both URLs get a second check on top of that, because a URL is the one thing
   * here that can carry a SCHEME. `a.href` is the dangerous one — a stored
   * `javascript:` there is a stored XSS — so only a same-origin path is taken,
   * which is all `product.url` ever produces. `img.src` cannot execute, but it
   * can beacon, so it is held to `https:` or the protocol-relative form.
   *
   * ⚠️ Protocol-relative is not an edge case, it is the NORMAL output: Liquid's
   * `image_url` returns `//shop.myshopify.com/cdn/shop/files/…`, with no scheme
   * at all. A first pass here tested for `https://…shopify.com/` and silently
   * rejected every real image, so every chip drew an empty circle.
   */
  const isPath = (u) => typeof u === 'string' && /^\/[^/\\]/.test(u);
  const isImage = (u) => typeof u === 'string' && (u.startsWith('//') || u.startsWith('https://'));

  function chip(item) {
    const li = document.createElement('li');

    const a = document.createElement('a');
    a.className = 'af-search__recent-link';
    a.href = item.u;

    const thumb = document.createElement('span');
    thumb.className = 'af-search__recent-thumb';
    if (isImage(item.i)) {
      const img = document.createElement('img');
      img.src = item.i;
      img.alt = '';
      img.width = 64;
      img.height = 64;
      img.loading = 'lazy';
      thumb.appendChild(img);
    }

    const text = document.createElement('span');
    text.className = 'af-search__recent-text';

    const name = document.createElement('span');
    name.className = 'af-search__recent-name';
    name.textContent = item.n || '';
    text.appendChild(name);

    // The category is the quiet second line and is dropped when the name IS the
    // category — see the snippet for the product that has no vehicle linked.
    if (item.c) {
      const cat = document.createElement('span');
      cat.className = 'af-search__recent-cat';
      cat.textContent = item.c;
      text.appendChild(cat);
    }

    a.append(thumb, text);
    li.appendChild(a);
    return li;
  }

  if (!customElements.get('airflux-recent')) {
    customElements.define(
      'airflux-recent',
      class AirfluxRecent extends HTMLElement {
        connectedCallback() {
          const list = this.querySelector('[data-airflux-recent-list]');
          if (!list) return;

          // The page you are standing on is not somewhere you have been. It is
          // still RECORDED above — leaving and coming back should find it — it
          // is only never drawn while you are on it.
          //
          // 🔴 Then deduped BY LABEL, not by handle, and the difference is the
          // whole reason this exists. Ford lists no generation for the Kuga on
          // either the product or the vehicle, so the 2008–2012 blades and the
          // 2020-onwards blades both label as "Ford Kuga · Wiper Blades" — with
          // the same photograph, because it is the same packaging. Somebody
          // comparing two Kuga generations got back two chips they could not
          // tell apart, which is a memory aid that has forgotten the one thing
          // it was there to remember.
          //
          // The years would separate them and are deliberately not used: a
          // vehicle's range is the union across every listing for that car (a
          // product can say 2020–2026 while its vehicle says 2020–2028), and
          // §5.3 refuses to print that against a specific product anywhere on
          // this store. So the chip IS its label, and two chips with one label
          // are one chip — the most recent one. Before the slice, so collapsing
          // a pair never costs the fourth chip its place.
          const seen = new Set();
          const items = read()
            .filter((p) => p?.n && isPath(p.u) && p.h !== here?.h)
            .filter((p) => {
              /* `\u0000` written as an escape, never as a literal NUL byte. It was
                 a raw one here, which made git classify the whole file as BINARY —
                 every diff on it came out as "Binary files differ" and could not be
                 reviewed. Same separator, same behaviour, and the file is text
                 again. It stays a control character on purpose: the two halves are
                 merchant-controlled strings, and any printable separator is one a
                 product title could contain and collide on. */
              const label = `${p.n}\u0000${p.c || ''}`;
              if (seen.has(label)) return false;
              seen.add(label);
              return true;
            })
            .slice(0, SHOWN);

          // Ships `hidden`, so a first visit never flashes an empty heading.
          if (!items.length) return;

          list.replaceChildren(...items.map(chip));
          this.hidden = false;
        }
      }
    );
  }
})();
